/**
 * Citations API - Access AI citation data for defensibility inspection.
 *
 * Every AI decision in ECA has verifiable citations back to source documents.
 * This module provides access to that citation data for the Citation Inspector UI.
 */

import { apiClient } from './client';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface Citation {
  id: string;
  document_id: string;
  document_filename: string | null;
  text_excerpt: string;
  start_offset: number | null;
  end_offset: number | null;
  citation_type: CitationType;
  context_type: string | null;
  relevance: number | null;
  is_verified: boolean;
  verification_method: VerificationMethod | null;
  verification_score: number | null;
  model_id: string | null;
  created_at: string;
}

export type CitationType =
  | 'triage'
  | 'privilege'
  | 'investigation'
  | 'rerank'
  | 'timeline';

export type VerificationMethod =
  | 'exact_match'
  | 'offset_match'
  | 'fuzzy_match'
  | 'unverified';

export interface CitationTypeStats {
  type: CitationType;
  total: number;
  verified: number;
  unverified: number;
}

export interface CitationStats {
  total_citations: number;
  verified_citations: number;
  unverified_citations: number;
  verification_rate: number;
  by_type: CitationTypeStats[];
  by_document: number;
}

export interface PaginatedCitations {
  items: Citation[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface CreateCitationRequest {
  document_id: string;
  matter_id: string;
  text_excerpt: string;
  citation_type?: CitationType;
  start_offset?: number;
  end_offset?: number;
  context_type?: string;
  relevance?: number;
  run_id?: string;
}

export interface ListCitationsParams {
  page?: number;
  page_size?: number;
  citation_type?: CitationType;
  document_id?: string;
  verified_only?: boolean;
  unverified_only?: boolean;
}

// -----------------------------------------------------------------------------
// API Functions
// -----------------------------------------------------------------------------

/**
 * Create a citation from a search result or manual annotation.
 *
 * The backend will auto-verify the citation against the source document
 * if start_offset and end_offset are provided.
 */
export async function createCitation(request: CreateCitationRequest): Promise<Citation> {
  const { data } = await apiClient.post<Citation>('/citations', request);
  return data;
}

/**
 * Get citation statistics for a matter.
 *
 * Returns aggregate counts and verification rates across all citation types.
 */
export async function getCitationStats(matterId: string): Promise<CitationStats> {
  const { data } = await apiClient.get<CitationStats>(`/citations/matters/${matterId}/citations/stats`);
  return data;
}

/**
 * List citations for a matter with pagination and filters.
 *
 * Citations are returned newest first.
 */
export async function listCitations(
  matterId: string,
  params?: ListCitationsParams
): Promise<PaginatedCitations> {
  const searchParams = new URLSearchParams();

  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.page_size) searchParams.set('page_size', params.page_size.toString());
  if (params?.citation_type) searchParams.set('citation_type', params.citation_type);
  if (params?.document_id) searchParams.set('document_id', params.document_id);
  if (params?.verified_only) searchParams.set('verified_only', 'true');
  if (params?.unverified_only) searchParams.set('unverified_only', 'true');

  const queryString = searchParams.toString();
  const url = `/citations/matters/${matterId}/citations${queryString ? `?${queryString}` : ''}`;

  const { data } = await apiClient.get<PaginatedCitations>(url);
  return data;
}

/**
 * Get a single citation by ID.
 */
export async function getCitation(citationId: string): Promise<Citation> {
  const { data } = await apiClient.get<Citation>(`/citations/citations/${citationId}`);
  return data;
}

/**
 * Get all citations for a specific document.
 *
 * Use this when viewing a document to show all AI decisions
 * that reference it with their supporting excerpts.
 */
export async function getDocumentCitations(
  documentId: string,
  citationType?: CitationType
): Promise<Citation[]> {
  const { data } = await apiClient.get<Citation[]>(
    `/citations/documents/${documentId}/citations`,
    citationType ? { params: { citation_type: citationType } } : undefined
  );
  return data;
}

// -----------------------------------------------------------------------------
// Namespace Export
// -----------------------------------------------------------------------------

export const citationsApi = {
  create: createCitation,
  getStats: getCitationStats,
  list: listCitations,
  get: getCitation,
  getForDocument: getDocumentCitations,
};
